import { lazy } from "react";

/**
 * Robust wrapper around React.lazy().
 *
 * Why:
 *   - In dev/preview, hot-reload rebuilds chunks with new hashes. If the
 *     browser has an old hash cached, navigating to a lazy route fires a
 *     "ChunkLoadError" — the URL updates but the target page never mounts,
 *     resulting in a silent blank screen.
 *   - In prod, the same thing happens right after a deploy for anyone with
 *     the previous bundle already loaded.
 *
 * Strategy:
 *   1. Retry the import a few times with a short backoff — handles flaky
 *      network / cold CDN.
 *   2. If it still fails AND we haven't already tried this trick for this
 *      page load, mark a session flag and do one full-page reload. That
 *      fetches the fresh index.html which points at the current chunk
 *      hashes, and the user lands on the intended URL.
 *   3. If even the post-reload attempt fails, surface the error normally
 *      so the top-level ErrorBoundary can show a friendly message.
 */
export default function lazyWithRetry(factory, retries = 2, delayMs = 400) {
  return lazy(() =>
    tryImport(factory, retries, delayMs).catch((err) => {
      const key = "__chunk_reload_attempted__";
      const alreadyReloaded = window.sessionStorage.getItem(key) === "1";
      const isChunkErr =
        err?.name === "ChunkLoadError" ||
        /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
          String(err?.message || err),
        );
      if (isChunkErr && !alreadyReloaded) {
        window.sessionStorage.setItem(key, "1");
        window.location.reload();
        // Return a never-resolving promise so Suspense keeps showing the
        // fallback until the reload takes effect.
        return new Promise(() => {});
      }
      throw err;
    }),
  );
}

async function tryImport(factory, retries, delayMs) {
  let lastErr;
  for (let i = 0; i <= retries; i++) {
    try {
      return await factory();
    } catch (e) {
      lastErr = e;
      if (i < retries) {
        await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
      }
    }
  }
  throw lastErr;
}
